import { Suspense } from 'react'

import { cn } from '@/utils'
import { ErrorBoundary } from '@sentry/react'
import { useAtomValue } from 'jotai'

import { RundownLoadingOverlay } from '@/entities/rundown/index.js'
import { rundownAtom } from '@/store/rundown.store.ts'

import { RundownHeader } from './header/RundownHeader.js'
import { RundownNavbar } from './navbar/RundownNavbar'
import { RundownBody } from './RundownBody.tsx'
import { RundownPageErrorBoundary } from './RundownPageErrorBoundary'

export function RundownPage() {
  return (
    <div data-label="layout-rundown">
      <RenderTitle />
      <div
        className={cn(
          'relative h-screen min-w-screen w-max flex flex-col text-white',
          // Touch: let the wrapper grow with content instead of being locked to 100vh,
          // so the document can be taller/wider than the visual viewport and pinch-zoom can reveal more.
          'pointer-coarse:h-auto pointer-coarse:min-h-screen',
        )}
      >
        <div
          className={cn(
            'w-screen px-2',
            // Touch: make the navbar sticky so it stays reachable while the document scrolls natively.
            'pointer-coarse:sticky pointer-coarse:top-0 pointer-coarse:z-50',
          )}
          style={{ height: '50px' }}
        >
          <RundownNavbar />
        </div>
        <ErrorBoundary fallback={RundownPageErrorBoundary}>
          <div
            id="rd-scroll-container"
            className={cn(
              'relative w-screen overflow-scroll',
              // Touch: drop inner overflow-scroll and fixed height so the page itself scrolls and pinch-zoom
              // can scale the actual document. `h-auto!` overrides the inline `calc(100vh - 50px)` below.
              'pointer-coarse:overflow-visible pointer-coarse:h-auto!',
              'overflow-anchor-none', // Disable overflow anchoring to prevent occasional scroll jumps when grid updates
            )}
            style={{
              height: 'calc(100vh - 50px)',
              scrollPadding: '108px 8px 8px', // top: size of rundown header + cue padding, rest: cue padding. Used by scrollIntoView() calls.
              ['--headers-size' as string]: '150px', // size of rundown header + sticky header
            }}
          >
            <header
              className={cn(
                'h-25 sticky top-0 z-40',
                // Prevent the container's 108px scroll-padding-top for elements inside header, to prevent grid scrolling on focus.
                '**:-scroll-mt-27',
                // Touch: the navbar above is sticky at top-0 (50px tall), so offset this header to sit below it.
                'pointer-coarse:top-12.5',
              )}
            >
              <RundownHeader />
            </header>
            <main className="w-full">
              <Suspense fallback={<RundownLoadingOverlay />}>
                <RundownBody />
              </Suspense>
            </main>
          </div>
        </ErrorBoundary>
      </div>
    </div>
  )
}

function RenderTitle() {
  const rundown = useAtomValue(rundownAtom)

  const title = `${rundown?.name} | Rundown Studio` // React 19 expects a string child only for <title>

  return <title>{title}</title>
}
